import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchPartner, trackPartnerScan, extractGenreLabel } from "../lib/api";
import { StoryCard } from "../components/StoryCard";

const groupByGenre = (stories) => {
  const groups = {};
  stories.forEach((s) => { (groups[s.genre || "autres"] = groups[s.genre || "autres"] || []).push(s); });
  return Object.entries(groups).sort(([a], [b]) => (a === "autres") - (b === "autres") || extractGenreLabel(a).localeCompare(extractGenreLabel(b)));
};

export default function PartnerLanding() {
  const { code } = useParams();
  const [partner, setPartner] = useState(null);
  const [error, setError] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    fetchPartner(code).then((p) => { setPartner(p); document.title = `Histoires offertes par ${p.name} | CLED`; }).catch(() => setError(true));
    const key = `cled_scan_${code}`;
    if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, "1"); trackPartnerScan(code); }
  }, [code]);

  if (error) return (
    <div data-testid="partner-not-found" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <h1 className="font-serif text-4xl text-[#1A1A1A] font-light mb-6">Ce lien n'est plus actif.</h1>
      <Link to="/histoires" className="bg-[#2C402E] text-[#FAF9F6] px-8 py-3 font-body text-sm">Voir toutes nos histoires</Link>
    </div>
  );
  if (!partner) return <div className="min-h-screen flex items-center justify-center font-body text-[#757575]">Chargement…</div>;

  return (
    <div data-testid="page-partner-landing" className="min-h-screen bg-[#FAF9F6]">
      <header className="border-b border-[#D1CFC7] bg-[#FAF9F6]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="partner-home-link"><img src="/cled-logo-cropped.png" alt="CLED" className="h-8 w-auto mix-blend-multiply" /></Link>
          {partner.logo_url && !logoFailed && <img src={partner.logo_url} alt={partner.name} onError={() => setLogoFailed(true)} data-testid="partner-landing-logo" className="h-9 w-auto max-w-[140px] object-contain" />}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 pt-10 pb-24">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E4735] font-body mb-3 fade-in-up">Lecture offerte par {partner.name}</p>
        <h1 data-testid="partner-landing-title" className="font-serif text-4xl sm:text-5xl font-light text-[#1A1A1A] leading-[1.05] mb-4 fade-in-up">Choisissez votre <em className="text-[#2C402E]">histoire</em></h1>
        <p className="font-body text-sm text-[#4A4A4A] mb-12 fade-in-up">Quelques minutes de lecture, ou d'écoute, pour rendre votre pause plus agréable. Gratuit, sans application.</p>
        {partner.extracts.length === 0 && <p data-testid="partner-no-stories" className="font-body text-[#757575]">Aucune histoire n'est disponible pour le moment.</p>}
        {groupByGenre(partner.extracts).map(([genre, stories]) => (
          <section key={genre} data-testid={`partner-genre-${genre}`} className="mb-12">
            <h2 className="flex items-center gap-4 font-serif italic text-2xl text-[#2C402E] mb-5"><span className="h-px w-8 bg-[#9E4735]" />{genre === "autres" ? "Autres histoires" : extractGenreLabel(genre)}</h2>
            <div className="grid grid-cols-1 gap-4">
              {stories.map((s, i) => <StoryCard key={s.id} story={s} index={i} href={`/histoires/${s.slug}?lieu=${partner.code}`} />)}
            </div>
          </section>
        ))}
        <footer className="mt-16 text-center font-body text-xs text-[#757575] space-y-2">
          <p>© Challenges Littéraires Éditions — Distributeur d'extraits littéraires CLED</p>
          <Link to="/livres" data-testid="partner-catalog-link" className="inline-block mt-2 font-semibold text-base text-[#2C402E] border-b-2 border-[#2C402E] pb-0.5">Découvrir tout notre catalogue</Link>
        </footer>
      </main>
    </div>
  );
}
